<template>
    <vProjectHeader
        logoColor="love"
        :categories="categories"
        :actions="dotsMenuActions"
        :project="wall"
        :shareLinks="shareLinks"
        :hasChanges="hasChanges"
        :hasSnapshot="hasSnapshot"
        :hidePublish="isCreatorShown"
        :hideShare="isCreatorShown"
        @toggleCategory="toggleCategory($event)"
        @publish="$emit('publish')"
    >
    </vProjectHeader>
</template>

<script>
import {
    StarOutlineIcon,
    LoveIcon,
    MailSendIcon,
    PaintbrushIcon,
    ArrowLeftIcon,
} from "@easy/ui/icons";
import { LOVE_PREVIEW_KIND, LOVE_ROUTE_NAMES } from "@/apps/love/love.routes";
import { mapGetters, mapState } from "vuex";
import vProjectHeader from "@/shared/views/vProjectHeader.vue";
import { shareUrlService } from "@/shared/projects/share-url.service";
import { storeToRefs } from "pinia";
import { useLoveCreatorStore } from "@/apps/love/project/onboarding/love-creator.store";
import { PAGE_ROUTE_NAMES } from "@/apps/page/page.routes";

export default {
    components: {
        vProjectHeader,
    },
    props: {
        actions: {
            type: Array,
            default: () => [],
        },
    },
    emits: ["publish"],
    setup() {
        const loveCreatorStore = useLoveCreatorStore();
        const { isCreatorShown, onboardState } = storeToRefs(loveCreatorStore);
        return { isCreatorShown, onboardState };
    },
    data() {
        return {
            openReviews: false,
            openStyles: false,
        };
    },
    computed: {
        ...mapState(["previewLang"]),
        ...mapState("love", ["wall", "testimonials", "hasChanges", "hasSnapshot"]),
        ...mapGetters("love", ["wallIframe"]),
        dotsMenuActions() {
            return this.isCreatorShown ? [] : this.actions;
        },
        reviews() {
            return this.testimonials || [];
        },
        categories() {
            const { page: projectUuid } = this.$route.query;
            const backToPageEdit =
                this.$route.query.page && !this.onboardState.isLoading
                    ? {
                          name: "page",
                          label: this.$t("page.menu.back"),
                          icon: ArrowLeftIcon,
                          handler: () => {
                              this.$router.push({
                                  name: PAGE_ROUTE_NAMES.projectSections,
                                  params: { projectUuid },
                              });
                          },
                      }
                    : {};
            if (this.isCreatorShown) {
                return [
                    backToPageEdit,
                    {
                        label: this.$t("shared.creator.create_new"),
                        name: "page",
                        icon: StarOutlineIcon,
                    },
                ];
            }
            return [
                backToPageEdit,
                {
                    name: "reviews",
                    label: this.$t("love.menu.reviews"),
                    icon: StarOutlineIcon,
                    isOpen: this.openReviews,
                    handler: () =>
                        this.routeTo(LOVE_ROUTE_NAMES.projectReviewsList, LOVE_PREVIEW_KIND.wall),
                    children: [
                        {
                            label: this.$t("love.menu.reviews_list"),
                            name: LOVE_ROUTE_NAMES.projectReviewsList,
                            handler: () =>
                                this.routeTo(
                                    LOVE_ROUTE_NAMES.projectReviewsList,
                                    LOVE_PREVIEW_KIND.wall
                                ),
                        },
                        {
                            label: this.$t("love.menu.request_review"),
                            name: LOVE_ROUTE_NAMES.projectRequestReview,
                            handler: () =>
                                this.routeTo(
                                    LOVE_ROUTE_NAMES.projectRequestReview,
                                    LOVE_PREVIEW_KIND.mail
                                ),
                        },
                        {
                            label: this.$t("love.menu.google_reviews"),
                            name: LOVE_ROUTE_NAMES.projectGoogleReviews,
                            handler: () =>
                                this.routeTo(
                                    LOVE_ROUTE_NAMES.projectGoogleReviews,
                                    LOVE_PREVIEW_KIND.wall
                                ),
                        },
                    ],
                },
                {
                    label: this.$t("love.menu.wall_of_love"),
                    icon: LoveIcon,
                    name: LOVE_ROUTE_NAMES.projectWall,
                    handler: () =>
                        this.routeTo(LOVE_ROUTE_NAMES.projectWall, LOVE_PREVIEW_KIND.wall),
                },
                {
                    label: this.$t("love.menu.review_form"),
                    icon: MailSendIcon,
                    name: LOVE_ROUTE_NAMES.projectForm,
                    handler: () =>
                        this.routeTo(LOVE_ROUTE_NAMES.projectForm, LOVE_PREVIEW_KIND.form),
                },
                {
                    label: this.$t("love.menu.styles"),
                    icon: PaintbrushIcon,
                    name: "styles",
                    isOpen: this.openStyles,
                    handler: () =>
                        this.routeTo(LOVE_ROUTE_NAMES.projectWallStyles, LOVE_PREVIEW_KIND.wall),
                    children: [
                        {
                            label: this.$t("love.menu.wall_styles"),
                            name: LOVE_ROUTE_NAMES.projectWallStyles,
                            handler: () =>
                                this.routeTo(
                                    LOVE_ROUTE_NAMES.projectWallStyles,
                                    LOVE_PREVIEW_KIND.wall
                                ),
                        },
                        {
                            label: this.$t("love.menu.form_styles"),
                            name: LOVE_ROUTE_NAMES.projectFormStyles,
                            handler: () =>
                                this.routeTo(
                                    LOVE_ROUTE_NAMES.projectFormStyles,
                                    LOVE_PREVIEW_KIND.form
                                ),
                        },
                    ],
                },
            ];
        },
        shareLinks() {
            const slug = this.wall.uuid;
            const wallURL = shareUrlService.makeUrlWithLang("walls", slug, this.previewLang);
            const reviewURL = shareUrlService.makeUrlWithLang("review-ask", slug, this.previewLang);
            return [
                {
                    value: "wall",
                    label: this.$t("love.views.wall"),
                    url: wallURL,
                    icon: LoveIcon,
                    code: this.wallIframe,
                },
                {
                    value: "form",
                    label: this.$t("love.views.form"),
                    url: reviewURL,
                    icon: MailSendIcon,
                    code: "",
                },
            ];
        },
    },
    methods: {
        routeTo(name, forcedView) {
            const { query } = this.$route;
            this.$router.push({ name, query: forcedView ? { ...query, view: forcedView } : query });
        },
        //FIXME: This is problem of passing computed categories
        toggleCategory(category) {
            if (category.name === "reviews") {
                this.openReviews = !this.openReviews;
            }
            if (category.name === "styles") {
                this.openStyles = !this.openStyles;
            }
        },
    },
};
</script>
